import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { Campaign, Scenario } from "@/components/sales-funnel/types";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export interface FunnelStats {
  funnel: { visitors: number; leads: number; prospects: number; clients: number };
  nanocorp: { total_missions: number; prospection: number; qualification: number; nurturing: number; redaction: number };
  campaigns: { total: number; active: number };
  prospects_by_status: Record<string, number>;
}

export interface FunnelAgent {
  agent_id: string;
  name: string;
  role: string;
  active: boolean;
  completed_missions: number;
  recent_logs: { action: string; log_type: string; created_at: string }[];
  latest_briefing: string;
  last_run: string;
}

export function useSalesFunnel() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [stats, setStats] = useState<FunnelStats | null>(null);
  const [agents, setAgents] = useState<FunnelAgent[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_URL}/api/funnel/campaigns`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.campaigns || []);
      }
    } catch {}
  }, [user]);

  const fetchScenarios = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_URL}/api/funnel/scenarios`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setScenarios(data.scenarios || []);
      }
    } catch {}
  }, [user]);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_URL}/api/funnel/stats`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch {}
  }, [user]);

  const fetchAgents = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_URL}/api/funnel/agent-activity`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAgents(data.agents || []);
      }
    } catch {}
  }, [user]);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    await Promise.all([fetchCampaigns(), fetchScenarios(), fetchStats(), fetchAgents()]);
    setLoading(false);
  }, [user, fetchCampaigns, fetchScenarios, fetchStats, fetchAgents]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const createCampaign = async (campaign: Omit<Campaign, "id">) => {
    try {
      const res = await fetch(`${API_URL}/api/funnel/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(campaign),
      });
      if (res.ok) {
        const data = await res.json();
        setCampaigns((prev) => [data, ...prev]);
        toast.success("Campagne créée !");
        fetchStats();
        return data;
      } else if (res.status === 401) {
        toast.error("Session expirée — reconnectez-vous");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur lors de la création");
      }
    } catch {
      toast.error("Erreur réseau — vérifiez votre connexion");
    }
    return null;
  };

  const updateCampaign = async (id: string, updates: Partial<Campaign>) => {
    const existing = campaigns.find((c) => c.id === id);
    if (!existing) return;
    const merged = { ...existing, ...updates };
    try {
      await fetch(`${API_URL}/api/funnel/campaigns/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(merged),
      });
      setCampaigns((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
    } catch {
      toast.error("Erreur de mise à jour");
    }
  };

  const deleteCampaign = async (id: string) => {
    try {
      await fetch(`${API_URL}/api/funnel/campaigns/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
      toast.success("Campagne supprimée");
      fetchStats();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const createScenario = async (scenario: Omit<Scenario, "id">) => {
    try {
      const res = await fetch(`${API_URL}/api/funnel/scenarios`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(scenario),
      });
      if (res.ok) {
        const data = await res.json();
        setScenarios((prev) => [data, ...prev]);
        toast.success("Scénario créé !");
        return data;
      }
    } catch {
      toast.error("Erreur lors de la création du scénario");
    }
    return null;
  };

  const updateScenario = async (id: string, updates: Partial<Scenario>) => {
    try {
      await fetch(`${API_URL}/api/funnel/scenarios/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
      setScenarios((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
    } catch {
      toast.error("Erreur de mise à jour du scénario");
    }
  };

  const deleteScenario = async (id: string) => {
    try {
      await fetch(`${API_URL}/api/funnel/scenarios/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      setScenarios((prev) => prev.filter((s) => s.id !== id));
      toast.success("Scénario supprimé");
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  return {
    campaigns, scenarios, stats, agents, loading,
    createCampaign, updateCampaign, deleteCampaign,
    createScenario, updateScenario, deleteScenario,
    refresh: fetchAll,
  };
}
